import { useCallback } from "react";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { CartProduct } from "../types/cart.types";
import { clearCart } from "../store/cartSlice";
import { clearWishlist } from "../store/wishlistSlice";

const showPaymentFailurePopup = () =>
  Swal.fire({
    icon: "error",
    title: "Something went wrong",
    text: "Payment failed. Please try again.",
    confirmButtonText: "OK",
  });

type ProceedFn = (
  mode: number,
  selectedItems: CartProduct[],
  addressCode?: string | null,
  totalAmount?: number,
) => Promise<any>;

interface UseCheckoutConfig {
  proceed: ProceedFn;
  onProcessingStart: () => void;
  onProcessingStop: () => void;
  onResetAuthSuccess: () => void;
  onRequireGuestAuth: () => void;
  onRequireRegister: () => void;
}

interface CheckoutArgs {
  mode: number;
  selectedAddressId: string | null;
  canPlaceOrder: boolean;
  hasOutOfStock: boolean;
  totalAmount: number;
  selectedProducts: CartProduct[];
}

/**
 * Orchestrates the cart checkout flow: validation, online payment, order
 * placement and the resulting dialogs. UI state (processing flags, modals)
 * is driven through the injected callbacks so the page keeps owning it.
 */
export const useCheckout = ({
  proceed,
  onProcessingStart,
  onProcessingStop,
  onResetAuthSuccess,
  onRequireGuestAuth,
  onRequireRegister,
}: UseCheckoutConfig) => {
  const dispatch = useDispatch();

  const handleProceed = useCallback(
    async ({
      mode,
      selectedAddressId,
      canPlaceOrder,
      hasOutOfStock,
      totalAmount,
      selectedProducts,
    }: CheckoutArgs) => {
      if (!selectedAddressId) {
        return Swal.fire({
          icon: "warning",
          title: "Select Address",
          text: "Please select a delivery address",
        });
      }

      if (!canPlaceOrder) {
        return Swal.fire({
          icon: "warning",
          title: "Cannot proceed",
          text: hasOutOfStock
            ? "Remove out-of-stock items to continue"
            : "Select at least one item",
        });
      }

      onProcessingStart();

      let result: any;
      try {
        result = await proceed(mode, selectedProducts, selectedAddressId, totalAmount);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        console.error("Error placing order:", error);

        await Swal.fire({
          icon: "error",
          title: "Order Failed",
          text: `Failed to place order: ${errorMessage}`,
          confirmButtonText: "OK",
        });
        return;
      } finally {
        onProcessingStop();
      }

      if (result.status === "not_verified") {
        const { isConfirmed, isDenied } = await Swal.fire({
          title: "Checkout",
          text: "How would you like to proceed?",
          icon: "question",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Continue as Guest",
          denyButtonText: "Login / Register",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#28a745",
          denyButtonColor: "#007bff",
        });

        onResetAuthSuccess();

        if (isConfirmed) {
          onRequireGuestAuth();
        } else if (isDenied) {
          onRequireRegister();
        }
        return;
      }

      if (result.status === "no_record") {
        const { isConfirmed } = await Swal.fire({
          title: "Something went wrong",
          text: "Please try again",
          icon: "error",
          confirmButtonText: "OK",
        });

        if (isConfirmed) {
          window.location.reload();
        }
        return;
      }

      if (result.status === "ok") {
        dispatch(clearCart());
        dispatch(clearWishlist());
        Swal.fire({ title: "Order placed! 🎉", icon: "success" }).then(
          () => (window.location.href = "/"),
        );
        return;
      }

      if (result.status === "payment_failed") {
        await showPaymentFailurePopup();
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Order failed",
        text: "Please try again",
        confirmButtonText: "OK",
      });
    },
    [
      proceed,
      dispatch,
      onProcessingStart,
      onProcessingStop,
      onResetAuthSuccess,
      onRequireGuestAuth,
      onRequireRegister,
    ],
  );

  return { handleProceed };
};
